// src/components/HomeworkScheduleGuideOverlay.js
import React, { useState, useLayoutEffect, useRef } from 'react';
import useGuideScrollExtension from '../utils/useGuideScrollExtension';

const BREAKPOINT = 900;
const TIP = 15;
const SPOT_PAD = 12;

const HomeworkScheduleGuideOverlay = ({
    onClose,
    mode      = 'manual',
    autoStep  = null,
    hasStudents = true,
    anonymousProfiles = false,
}) => {
    const [isSmallScreen, setIsSmallScreen] = useState(() => window.innerWidth < BREAKPOINT);
    const [positions, setPositions] = useState(null);
    const box1Ref = useRef(null); // Add Your Students
    const box2Ref = useRef(null); // Edit Student Profiles
    const box3Ref = useRef(null); // Create Tutorials
    const box4Ref = useRef(null); // View Tutorial (link)
    const box5Ref = useRef(null); // Tutorial Report

    useLayoutEffect(() => {
        const calculate = () => {
            const vw = window.innerWidth;
            const vh = window.innerHeight;

            if (vw < BREAKPOINT) {
                setIsSmallScreen(true);
                setPositions(null);
                return;
            }
            setIsSmallScreen(false);

            const active = mode === 'auto'
                ? new Set([autoStep])
                : new Set([1, 2, 3, 4, 5]);

            const studentBtnEl     = document.querySelector('[data-guide="hs-add-student"]');
            const editBtnEl        = document.querySelector('[data-guide="hs-edit-student"]');
            const createBtnEl      = document.querySelector('[data-guide="hs-create-tutorial"]');
            const tutorialLinkEl   = document.querySelector('[data-guide="hs-tutorial-link"]');
            const tutorialReportEl = document.querySelector('[data-guide="hs-tutorial-report"]');

            if (!box1Ref.current || !box2Ref.current || !box3Ref.current || !box4Ref.current || !box5Ref.current) return;

            const b1 = box1Ref.current.getBoundingClientRect();
            const b2 = box2Ref.current.getBoundingClientRect();
            const b3 = box3Ref.current.getBoundingClientRect();
            const b4 = box4Ref.current.getBoundingClientRect();
            const b5 = box5Ref.current.getBoundingClientRect();
            const pad = 10;
            const newPos = {};

            // ── Box 1: Add Your Students — arrowDown ──────────────────────────────────────
            if (active.has(1) && studentBtnEl) {
                const se    = studentBtnEl.getBoundingClientRect();
                const tipX  = se.left + se.width / 2;
                const tipY  = se.top;
                const boxLeft   = Math.max(pad, Math.min(tipX - b1.width / 2, vw - b1.width - pad));
                const arrowLeft = Math.min(Math.max(TIP, tipX - boxLeft), b1.width - TIP);
                newPos.box1 = { bottom: vh - tipY + TIP, left: boxLeft, arrowLeft };
            }

            // ── Box 2: Edit Student Profiles — arrowRight ─────────────────────────────────
            if (active.has(2) && editBtnEl) {
                const ee    = editBtnEl.getBoundingClientRect();
                const tipX  = ee.left;
                const tipY  = ee.top + ee.height / 2;
                const boxLeft = Math.max(pad, tipX - TIP - b2.width);
                const boxTop  = tipY - b2.height / 2;
                newPos.box2 = { top: boxTop, left: boxLeft };
            }

            // ── Box 3: Create Tutorials — arrowUp (bubble below button) ───────────────────
            if (active.has(3) && createBtnEl) {
                const ce       = createBtnEl.getBoundingClientRect();
                const tipX     = ce.left + ce.width / 2;
                const tipY     = ce.bottom;
                const boxLeft  = Math.max(pad, Math.min(tipX - b3.width / 2, vw - b3.width - pad));
                const arrowLeft = Math.min(Math.max(TIP, tipX - boxLeft), b3.width - TIP);
                newPos.box3 = { top: tipY + TIP, left: boxLeft, arrowLeft };
            }

            // ── Box 4: View Tutorial — arrowDown, tip at tutorial link button ─────────────
            if (active.has(4) && tutorialLinkEl) {
                const tl    = tutorialLinkEl.getBoundingClientRect();
                const tipX  = tl.left + tl.width / 2;
                const tipY  = tl.top;
                const boxLeft   = Math.max(pad, Math.min(tipX - b4.width / 2, vw - b4.width - pad));
                const arrowLeft = Math.min(Math.max(TIP, tipX - boxLeft), b4.width - TIP);
                newPos.box4 = { bottom: vh - tipY + TIP, left: boxLeft, arrowLeft };
            }

            // ── Box 5: Tutorial Report — arrowUp (bubble below button) ───────────────────
            if (active.has(5) && tutorialReportEl) {
                const tr    = tutorialReportEl.getBoundingClientRect();
                const tipX  = tr.left + tr.width / 2;
                const boxLeft   = Math.max(pad, Math.min(tipX - b5.width / 2, vw - b5.width - pad));
                const arrowLeft = Math.min(Math.max(TIP, tipX - boxLeft), b5.width - TIP);
                newPos.box5 = { top: tr.bottom + TIP, left: boxLeft, arrowLeft };
            }

            // ── Spotlight ──────────────────────────────────────────────────────────────────
            if (mode === 'auto') {
                let targetEl = null;
                if (autoStep === 1)      targetEl = studentBtnEl;
                else if (autoStep === 2) targetEl = editBtnEl;
                else if (autoStep === 3) targetEl = createBtnEl;
                else if (autoStep === 4) targetEl = tutorialLinkEl;
                else if (autoStep === 5) targetEl = tutorialReportEl;
                if (targetEl) {
                    const r = targetEl.getBoundingClientRect();
                    newPos.spotlights = [{ x: r.left, y: r.top, w: r.width, h: r.height }];
                }
            }

            setPositions(newPos);
        };

        calculate();
        window.addEventListener('resize', calculate);
        window.addEventListener('scroll', calculate, { passive: true });
        return () => {
            window.removeEventListener('resize', calculate);
            window.removeEventListener('scroll', calculate);
        };
    }, [mode, autoStep, hasStudents, anonymousProfiles]);

    useGuideScrollExtension(!isSmallScreen);

    if (isSmallScreen) return null;

    // ── Styles ───────────────────────────────────────────────────────────────────

    const overlayStyle = {
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        zIndex: 9999,
        cursor: 'pointer',
        ...(mode === 'manual' ? { backgroundColor: 'rgba(45, 64, 66, 0.6)' } : {}),
        fontFamily: '"Comic Sans MS", "Chalkboard SE", "Marker Felt", sans-serif',
    };

    const spots = positions?.spotlights ?? [];

    const boxBase = {
        position: 'absolute',
        backgroundColor: 'white',
        border: '3px solid #2D4042',
        borderRadius: '255px 15px 225px 15px / 15px 225px 15px 255px',
        padding: '20px',
        boxShadow: '8px 8px 0px rgba(0,0,0,0.3)',
        pointerEvents: mode === 'auto' ? 'auto' : 'none',
    };

    const markerStyle = {
        position: 'absolute', top: '-15px', left: '-15px',
        width: '35px', height: '35px', backgroundColor: '#75B9BE', color: 'white',
        borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 'bold', fontSize: '1.2rem', border: '2px solid #2D4042',
        boxShadow: '3px 3px 0px rgba(0,0,0,0.2)',
    };

    const titleStyle = {
        margin: '0 0 5px 0', color: '#2D4042', fontSize: '1.1rem',
        fontWeight: '900', textTransform: 'uppercase', letterSpacing: '1px',
    };

    const textStyle = { margin: 0, color: '#555', fontSize: '0.95rem', lineHeight: '1.4' };

    const getBoxStyle = (posData, maxWidth) => {
        if (!posData) return { ...boxBase, maxWidth, visibility: 'hidden', top: -9999, left: -9999 };
        const s = { ...boxBase, maxWidth, visibility: 'visible' };
        if (posData.left   !== undefined) s.left   = posData.left;
        if (posData.top    !== undefined) s.top    = posData.top;
        if (posData.bottom !== undefined) s.bottom = posData.bottom;
        return s;
    };

    const makeArrowDown = (arrowLeft) => ({
        position: 'absolute', bottom: '-15px', left: arrowLeft ?? '50%',
        width: 0, height: 0,
        borderLeft: '15px solid transparent', borderRight: '15px solid transparent',
        borderTop: '15px solid white', filter: 'drop-shadow(0px 3px 0px #2D4042)',
    });

    const arrowRightStyle = {
        position: 'absolute', top: '50%', right: '-15px',
        width: 0, height: 0,
        borderTop: '10px solid transparent', borderBottom: '10px solid transparent',
        borderLeft: '15px solid white', transform: 'translateY(-50%)',
        filter: 'drop-shadow(3px 0px 0px #2D4042)',
    };

    const makeArrowUp = (arrowLeft) => ({
        position: 'absolute', top: '-15px', left: arrowLeft ?? '50%',
        width: 0, height: 0,
        borderLeft: '15px solid transparent', borderRight: '15px solid transparent',
        borderBottom: '15px solid white', filter: 'drop-shadow(0px -3px 0px #2D4042)',
    });

    const closeBtnStyle = {
        position: 'absolute', top: '6px', right: '8px',
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: '1.1rem', color: '#2D4042', fontWeight: 'bold', lineHeight: 1, padding: '2px 4px',
    };

    return (
        <div style={overlayStyle} onClick={onClose}>

            {mode === 'auto' && (
                <svg style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                    <style>{`
                        @keyframes guide-pulse {
                            0%, 100% { opacity: 1; }
                            50% { opacity: 0.4; }
                        }
                        .guide-spotlight-ring { animation: guide-pulse 1.8s ease-in-out infinite; }
                    `}</style>
                    <defs>
                        <mask id="guide-spotlight-mask">
                            <rect width="100%" height="100%" fill="white" />
                            {spots.map((s, i) => (
                                <rect key={i}
                                    x={s.x - SPOT_PAD} y={s.y - SPOT_PAD}
                                    width={s.w + SPOT_PAD * 2} height={s.h + SPOT_PAD * 2}
                                    rx="8" fill="black"
                                />
                            ))}
                        </mask>
                    </defs>
                    <rect width="100%" height="100%" fill="rgba(45,64,66,0.78)" mask="url(#guide-spotlight-mask)" />
                    {spots.map((s, i) => (
                        <rect key={i}
                            className="guide-spotlight-ring"
                            x={s.x - SPOT_PAD} y={s.y - SPOT_PAD}
                            width={s.w + SPOT_PAD * 2} height={s.h + SPOT_PAD * 2}
                            rx="8" fill="none" stroke="#75B9BE" strokeWidth="3"
                        />
                    ))}
                </svg>
            )}

            {/* Step 1: Add Your Students 👥 */}
            <div data-guide-popup="true" ref={box1Ref} style={getBoxStyle(positions?.box1, '270px')}>
                {mode === 'auto' && <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>}
                <div style={markerStyle}>👥</div>
                <p style={titleStyle}>1. {anonymousProfiles ? 'Add Anonymous Profiles' : 'Assign Your Students'}</p>
                <p style={textStyle}>
                    {anonymousProfiles
                        ? 'Click here to add anonymous learner profiles. Each profile represents a set of learning needs — you only need to set this up once per class type.'
                        : 'Click here to create a profile for each of your students. This only needs to be done once, after which you can use the buttons to the left to add individual students or whole classes to each Lesson Plan.'}
                </p>
                <div style={makeArrowDown(positions?.box1?.arrowLeft)} />
            </div>

            {/* Step 2: Edit Student Profiles ✏️ */}
            <div data-guide-popup="true" ref={box2Ref} style={getBoxStyle(positions?.box2, '270px')}>
                {mode === 'auto' && <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>}
                <div style={markerStyle}>✏️</div>
                <p style={titleStyle}>2. {anonymousProfiles ? 'Edit Anonymous Profiles' : 'Edit Student Profiles'}</p>
                <p style={textStyle}>
                    {anonymousProfiles
                        ? 'Click <strong>Edit</strong> on any profile to adjust reading age, SEN conditions, EAL status, and more. ClassLabs uses these to scaffold every tutorial appropriately.'
                        : <span>Click <strong>Edit</strong> on any student to update their profile — adjust reading age, SEN conditions, EAL status, font preferences, and more. ClassLabs uses these details to add the right scaffolding to every tutorial.</span>}
                </p>
                <div style={arrowRightStyle} />
            </div>

            {/* Step 3: Create Tutorials ✨ */}
            <div data-guide-popup="true" ref={box3Ref} style={getBoxStyle(positions?.box3, '260px')}>
                {mode === 'auto' && <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>}
                <div style={markerStyle}>✨</div>
                <p style={titleStyle}>3. Create Tutorials</p>
                <p style={textStyle}>
                    {anonymousProfiles
                        ? 'Once you have added all the profiles you need, click '
                        : 'Once you have added all the students you want to study this Lesson Plan, click '}
                    <strong>+ Create</strong> to build personalised tutorials{anonymousProfiles ? ' for each profile!' : ' for every student in your class!'}
                </p>
                <div style={makeArrowUp(positions?.box3?.arrowLeft)} />
            </div>

            {/* Step 4: View Tutorial 🔗 */}
            <div data-guide-popup="true" ref={box4Ref} style={getBoxStyle(positions?.box4, '270px')}>
                {mode === 'auto' && <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>}
                <div style={markerStyle}>🔗</div>
                <p style={titleStyle}>4. View Tutorial</p>
                <p style={textStyle}>
                    Access created tutorials here to preview the content or generate a unique link to share directly with the student.
                </p>
                <div style={makeArrowDown(positions?.box4?.arrowLeft)} />
            </div>

            {/* Step 5: Tutorial Report 📊 */}
            <div data-guide-popup="true" ref={box5Ref} style={getBoxStyle(positions?.box5, '270px')}>
                {mode === 'auto' && <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>}
                <div style={markerStyle}>📊</div>
                <p style={titleStyle}>5. Class Report</p>
                <p style={textStyle}>
                    Once a class have completed their tutorials, click <strong>Class Report</strong> to see an overview of learning objectives and vocabulary understanding across students!
                </p>
                <div style={makeArrowUp(positions?.box5?.arrowLeft)} />
            </div>

            <p style={{ position: 'fixed', bottom: '20px', width: '100%', textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: '1.2rem', textShadow: '2px 2px 4px rgba(0,0,0,0.5)', pointerEvents: 'none' }}>
                Click anywhere to close
            </p>
        </div>
    );
};

export default HomeworkScheduleGuideOverlay;
